import { useMemo } from 'react';
import type { NormalizedItem, SortCriteria } from '@/components/itemList/types';

const SORT_CRITERIA_MAP: Record<number, SortCriteria> = {
  0: 'date',
  1: 'name',
  2: 'type',
};

/**
 * Sort an array of normalized items. Returns a new array.
 * This is the single source of truth for item ordering — used by both
 * the useSortedItems hook (list/gallery views) and ResourcePage (browse mode).
 */
export function sortItems(
  items: NormalizedItem[],
  criteriaIndex: number,
  ascending: boolean,
): NormalizedItem[] {
  if (!items || items.length === 0) return items;
  const criteria = SORT_CRITERIA_MAP[criteriaIndex] ?? 'date';
  const sorted = [...items];

  sorted.sort((a, b) => {
    switch (criteria) {
      case 'date': {
        if (a.created < b.created) return ascending ? 1 : -1;
        if (a.created > b.created) return ascending ? -1 : 1;
        return 0;
      }
      case 'name': {
        const nameA = (a.title || a.name || '').toLowerCase();
        const nameB = (b.title || b.name || '').toLowerCase();
        if (nameA < nameB) return ascending ? -1 : 1;
        if (nameA > nameB) return ascending ? 1 : -1;
        return 0;
      }
      case 'type': {
        if (a.itemtype === 'resource' && b.itemtype !== 'resource') {
          return ascending ? 1 : -1;
        }
        if (a.itemtype !== 'resource' && b.itemtype === 'resource') {
          return ascending ? -1 : 1;
        }
        const typeA = (a.itemtype === 'resource' ? a.type : a.itemtype) || '';
        const typeB = (b.itemtype === 'resource' ? b.type : b.itemtype) || '';
        if (typeA < typeB) return ascending ? -1 : 1;
        if (typeA > typeB) return ascending ? 1 : -1;
        return 0;
      }
      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * React hook wrapper around sortItems. Memoizes the result.
 */
export function useSortedItems(
  items: NormalizedItem[],
  criteriaIndex: number,
  ascending: boolean,
): NormalizedItem[] {
  return useMemo(
    () => sortItems(items, criteriaIndex, ascending),
    [items, criteriaIndex, ascending],
  );
}
